import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
  Fade,
  Box,
  Typography,
  Chip,
  Stack,
  CircularProgress
} from "@mui/material";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { API_URL, popupTimer } from "../../consts";

// Import sub-components
import TitleSection from "./TitleSection";
import LeftContent from "./LeftContent";
import RightInfoPanel from "./RightInfoPanel";
import DialogActionsSection from "./DialogActionsSection";
import CommentsDialog from "./CommentsDialog";
import SummaryEditorDialog from "../Ai/SummaryEditorDialog";

const HomeCardDialog = ({
  open,
  onClose,
  data,
  onReadMore,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [dialogData, setDialogData] = useState(data);
  const [originalData, setOriginalData] = useState(data);
  const authorized = useSelector((state) => state.authorized);
  const userData = useSelector((state) => state.user);
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [showSummaryEditor, setShowSummaryEditor] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [rightPanelData, setRightPanelData] = useState({
    comment: data.comment || "",
    average_stars: data.average_stars || 0,
    rating_id: data.rating_id
  });
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageFile, setPreviewImageFile] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewImageArrayBuffer, setPreviewImageArrayBuffer] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Check if the current user owns this rate
  // Try multiple ways to get user name
  const currentUserName = userData?.user_name || userData?.username || userData?.name;
  const ratingUserName = dialogData.user_name || dialogData.username || data.user_name || data.username;
  const isOwner = authorized && currentUserName && ratingUserName && (currentUserName === ratingUserName);

  useEffect(() => {
    setDialogData(data);
    setOriginalData(data);
    setRightPanelData({
      comment: data.comment || "",
      average_stars: data.average_stars || 0,
      rating_id: data.rating_id
    });
 
    if (isOwner && editMode) {
      setEditMode(false);
    }
    
  }, [data, authorized, userData, editMode]);

  const getDomain = (url) => {
    try {
      if (!url) return '';
      const parsedUrl = new URL(url);
      return parsedUrl.hostname;
    } catch (error) {
      return '';
    }
  };

  const handleUpdate = async (updatedFields) => {
    try {
      let payload = {
        stars: rightPanelData.average_stars,
        average_stars: rightPanelData.average_stars,
        title: updatedFields.title,
        comment: rightPanelData.comment,
      };

      // If we're updating the rating, make sure to use the new rating value
      if (updatedFields.average_stars !== undefined) {
        payload.stars = updatedFields.average_stars;
        payload.average_stars = updatedFields.average_stars;
      }

      // If we're updating the comment, make sure to use the new comment value
      if (updatedFields.comment !== undefined) {
        payload.comment = updatedFields.comment;
      }

      // Determine which ID to use for the API call
      const ratingId = updatedFields.rating_id || dialogData.id;

      await axios.put(
        `${API_URL}/rates/ratings/${ratingId}`,
        payload
      );

      // Fetch the updated rating to get fresh data from server (including image_url)
      try {
        const { data: responseData } = await axios.get(`${API_URL}/rates/ratings/${ratingId}`);
        // Merge only relevant fields from rating response (don't overwrite user_name, etc.)
        const updatedDialogData = mergeRatingUpdate(dialogData, responseData);
        const updatedOriginalData = mergeRatingUpdate(originalData, responseData);
        
        // Update dialog data with merged data (preserves user info, etc.)
        setDialogData(updatedDialogData);
        setOriginalData(updatedOriginalData);
        
        // Notify parent component about the update
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate(updatedDialogData);
        }
      } catch (fetchError) {
        console.error("Error fetching updated rating:", fetchError);
        // Fallback to local update if fetch fails
        setDialogData({ ...dialogData, ...payload });
      }

      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "Rating updated successfully!",
          type: "success",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
    } catch (err) {
      if (err?.response?.status === 401) {
        dispatch({ type: "SET_UNAUTHORIZED" });
        return;
      }
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: err?.response?.data?.error
            ? err.response.data.error
            : "Failed to update rating. Try again later.",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
    }
  };

  const handleSpaceClick = async (ratingId) => {
    try {
      const { data: responseData } = await axios.get(`${API_URL}/rates/ratings/${ratingId}`);
      setDialogData(responseData);
    } catch (error) {
      console.error("Error fetching space data:", error);
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "Error loading space data",
          type: "error",
        },
      });
    }
  };

  const handleBackToAll = () => {
    if (dialogData.id === originalData.id) {
      return;
    }
    setDialogData(originalData);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onClose(e);
  };

  const handleRightPanelUpdate = (data) => {
    setRightPanelData(data);
  };

  // Helper function to merge only relevant fields from rating response
  // This prevents overwriting fields that don't exist in rating endpoint (like user_name, first_name, etc.)
  const mergeRatingUpdate = (currentData, ratingResponse) => {
    const updatedData = { ...currentData };
    
    // Only update fields that exist in the rating response
    if (ratingResponse.image_url !== undefined) {
      updatedData.image_url = ratingResponse.image_url;
    }
    if (ratingResponse.comment !== undefined) {
      updatedData.comment = ratingResponse.comment;
    }
    if (ratingResponse.title !== undefined) {
      updatedData.title = ratingResponse.title;
    }
    if (ratingResponse.stars !== undefined) {
      updatedData.stars = ratingResponse.stars;
    }
    if (ratingResponse.average_stars !== undefined) {
      updatedData.average_stars = ratingResponse.average_stars;
    }
    if (ratingResponse.tags !== undefined) {
      updatedData.tags = ratingResponse.tags;
    }
    if (ratingResponse.rating_id !== undefined) {
      updatedData.rating_id = ratingResponse.rating_id;
    }
    if (ratingResponse.id !== undefined) {
      updatedData.id = ratingResponse.id;
    }
    
    return updatedData;
  };

  const handleEditImage = () => {
    document.getElementById("image-edit-input").click();
  };

  const imageChange = (e) => {
    const image = e.target.files[0];
    if (!image) return;

    // Validate file size (max 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (image.size > MAX_FILE_SIZE) {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "Image size exceeds 10MB limit",
          type: "error",
        },
      });
      setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
      return;
    }

    // Validate file type
    if (!image.type.startsWith('image/')) {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "Please select a valid image file",
          type: "error",
        },
      });
      setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(image);
    setPreviewImageUrl(previewUrl);
    setPreviewImageFile(image);

    // Read file as ArrayBuffer for later upload
    const reader = new FileReader();
    
    reader.onload = () => {
      setPreviewImageArrayBuffer(reader.result);
      setImagePreviewOpen(true);
    };

    reader.onerror = () => {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "File reading failed. Please try another image.",
          type: "error",
        },
      });
      setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
    };

    // Read file as ArrayBuffer for binary upload
    reader.readAsArrayBuffer(image);
  };

  const handleUploadImage = async () => {
    if (!previewImageFile || !previewImageArrayBuffer) return;

    setUploading(true);
    try {
      // Convert ArrayBuffer to base64
      const bytes = new Uint8Array(previewImageArrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64String = btoa(binary);
      const base64Data = `data:${previewImageFile.type};base64,${base64String}`;

      // Determine which ID to use for the API call
      const ratingId = rightPanelData.rating_id || dialogData.id;

      await axios.put(
        `${API_URL}/rates/ratings/${ratingId}`,
        {
          image: base64Data
        }
      );

      // Clean up preview
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
      setImagePreviewOpen(false);
      setPreviewImageFile(null);
      setPreviewImageUrl(null);
      setPreviewImageArrayBuffer(null);
      
      // Immediately fetch the updated rating to get the new image URL from server
      try {
        const { data: responseData } = await axios.get(`${API_URL}/rates/ratings/${ratingId}`);
        // Merge only relevant fields from rating response (don't overwrite user_name, etc.)
        const updatedDialogData = mergeRatingUpdate(dialogData, responseData);
        const updatedOriginalData = mergeRatingUpdate(originalData, responseData);
        
        // Update dialog data with merged data (preserves user info, etc.)
        setDialogData(updatedDialogData);
        // Also update originalData so it reflects the new image
        setOriginalData(updatedOriginalData);
        
        // Notify parent component about the update so it can refresh the rates list
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate(updatedDialogData);
        }
        
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "Image updated successfully!",
            type: "success",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      } catch (fetchError) {
        console.error("Error fetching updated rating:", fetchError);
        // Even if fetch fails, show success message since upload succeeded
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "Image uploaded successfully!",
            type: "success",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      }
    } catch (err) {
      console.error("Error uploading image", err);
      if (err?.response?.status === 401) {
        dispatch({ type: "SET_UNAUTHORIZED" });
        return;
      }
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: err.response?.data?.error || "Failed to upload image",
          type: "error",
        },
      });
      setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
    } finally {
      setUploading(false);
    }
  };

  const handleCancelPreview = () => {
    // Clean up preview URL
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
    }
    setImagePreviewOpen(false);
    setPreviewImageFile(null);
    setPreviewImageUrl(null);
    setPreviewImageArrayBuffer(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: "90vw", md: "85vw", lg: "80vw" },
            height: { xs: "100vh", sm: "90vh", md: "85vh" },
            maxHeight: { xs: "100vh", sm: "90vh" },
            margin: { xs: 0, sm: "auto" },
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            background: "linear-gradient(145deg, #ffffff, #f5f5f5)",
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: isMobile ? "flex-start" : "center",
            background: "rgba(0, 0, 0, 0.5)",
            padding: { xs: 0, sm: 2 },
          },
          "& .MuiDialog-paper": {
            width: { xs: "100vw", sm: "90vw", md: "85vw", lg: "80vw" },
            maxWidth: { xs: "100vw", sm: "90vw" },
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <TitleSection
          title={dialogData.title}
          url={dialogData.page_id?.href}
          getDomain={getDomain}
          onUpdate={handleUpdate}
          authorized={authorized && isOwner}
          editMode={editMode}
          onEditModeChange={setEditMode}
          rightPanelData={rightPanelData}
          onRightPanelUpdate={handleRightPanelUpdate}
        />

        <DialogContent
          sx={{
            height: { xs: "calc(100vh - 140px)", sm: "calc(100% - 140px)", md: "calc(100% - 130px)" },
            p: { xs: 1.5, sm: 2, md: 3 },
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            overflow: "hidden", // Prevent dialog content from scrolling
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "4px",
              "&:hover": {
                background: "#555",
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              height: "100%",
              width: "100%",
              maxWidth: { xs: "100%", sm: "1200px" },
              gap: { xs: 1.5, sm: 2, md: 3 },
              alignItems: "flex-start",
              justifyContent: "center",
            }}
          >
            <Box sx={{ 
              flex: { xs: "0 1 auto", sm: 1 }, 
              display: "flex", 
              justifyContent: "center",
              width: { xs: "100%", sm: "auto" },
              height: { xs: "auto", sm: "100%" },
              maxHeight: { xs: "50vh", sm: "100%" },
              overflow: "hidden", // Prevent this container from scrolling
            }}>
              <LeftContent 
                data={dialogData} 
                onReadMore={onReadMore}
                onEditImage={handleEditImage}
                canEdit={isOwner}
              />
              <input
                accept="image/*"
                id="image-edit-input"
                type="file"
                onChange={imageChange}
                style={{ display: "none" }}
              />
            </Box>
            <RightInfoPanel
              data={dialogData}
              onUpdate={handleUpdate}
              authorized={authorized}
              onOpenComments={() => setShowCommentsDialog(true)}
              onSpaceClick={handleSpaceClick}
              onBackToAll={handleBackToAll}
              showBackButton={dialogData.id !== originalData.id}
              editMode={editMode}
              onEditModeChange={setEditMode}
              rightPanelData={rightPanelData}
              onRightPanelUpdate={handleRightPanelUpdate}
            />
          </Box>
        </DialogContent>

        <DialogActionsSection 
          handleClose={handleClose} 
          onReadingMode={() => setShowSummaryEditor(true)} 
        />
      </Dialog>

      <CommentsDialog
        open={showCommentsDialog}
        onClose={() => setShowCommentsDialog(false)}
        rateId={data.id}
      />

      <SummaryEditorDialog
        open={showSummaryEditor}
        onClose={() => setShowSummaryEditor(false)}
        onSave={(content) => {
          // Handle saving the summary content
          setShowSummaryEditor(false);
        }}
        objectId={data.id}
      />

      {/* Image Preview Dialog */}
      <Dialog 
        open={imagePreviewOpen} 
        onClose={handleCancelPreview}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Preview Image</DialogTitle>
        <DialogContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2,
            py: 2
          }}>
            {previewImageUrl && (
              <Box
                component="img"
                src={previewImageUrl}
                alt="Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: 2,
                  objectFit: 'contain',
                  border: '1px solid #e0e0e0'
                }}
              />
            )}
            {previewImageFile && (
              <Typography variant="body2" color="textSecondary">
                File: {previewImageFile.name} ({(previewImageFile.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
            <Box
              sx={{
                width: '100%',
                p: 2,
                mt: 1,
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: 1,
                borderLeft: '4px solid #ffc107'
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.875rem',
                  color: '#856404',
                  fontWeight: 500,
                  lineHeight: 1.5
                }}
              >
                <strong>Content Policy:</strong> By uploading this image, you confirm that it does not contain sexual, illegal, violent content, or content you do not own the copyright to. Uploading such content violates our Terms of Service and may result in account suspension or legal action.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPreview} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUploadImage} 
            variant="contained"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HomeCardDialog;
